import { useRef, useState } from 'react'
import { DIFFICULTIES, type Difficulty } from '../config/ai-difficulty.ts'
import bg from '../assets/bg2.webp'
import { t } from '../i18n/translations.ts'
import { useStamina } from '../hooks/useStamina.ts'

interface Props {
  initial: Difficulty
  onStart: (d: Difficulty) => void
  onBack: () => void
}

// 上から難しい順（上: HELL、下: EASY）で縦に並べる
const DIFFICULTY_ORDER: readonly Difficulty[] = ['hell', 'hard', 'normal', 'easy']

function difficultyName(difficulty: Difficulty): string {
  switch (difficulty) {
    case 'easy':
      return t('difficultyEasyName')
    case 'normal':
      return t('difficultyNormalName')
    case 'hard':
      return t('difficultyHardName')
    case 'hell':
      return t('difficultyHellName')
  }
}

function difficultyDescription(difficulty: Difficulty): string {
  switch (difficulty) {
    case 'easy':
      return t('difficultyEasyDescription')
    case 'normal':
      return t('difficultyNormalDescription')
    case 'hard':
      return t('difficultyHardDescription')
    case 'hell':
      return t('difficultyHellDescription')
  }
}

/**
 * アクセシビリティ: radiogroup パターンで単一選択を表現する。
 * - 各カードは role=radio + aria-checked
 * - 選択肢の説明は aria-describedby で吹き出しと関連付け
 * - ArrowUp/ArrowDown で順に移動、Home/End で両端にジャンプ
 */
export function DifficultyScreen({ initial, onStart, onBack }: Props) {
  const [selected, setSelected] = useState<Difficulty>(initial)
  const { canPlay, consume } = useStamina()
  const buttonRefs = useRef<Record<Difficulty, HTMLButtonElement | null>>({
    hell: null,
    hard: null,
    normal: null,
    easy: null,
  })

  const focusDifficulty = (d: Difficulty) => {
    setSelected(d)
    buttonRefs.current[d]?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const index = DIFFICULTY_ORDER.indexOf(selected)
    if (index < 0) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      focusDifficulty(DIFFICULTY_ORDER[Math.min(index + 1, DIFFICULTY_ORDER.length - 1)])
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault()
      focusDifficulty(DIFFICULTY_ORDER[Math.max(index - 1, 0)])
    } else if (e.key === 'Home') {
      e.preventDefault()
      focusDifficulty(DIFFICULTY_ORDER[0])
    } else if (e.key === 'End') {
      e.preventDefault()
      focusDifficulty(DIFFICULTY_ORDER[DIFFICULTY_ORDER.length - 1])
    }
  }

  const handleStart = () => {
    if (!canPlay) return
    const consumed = consume('npc')
    if (consumed) onStart(selected)
  }

  return (
    <div
      className="screen screen--difficulty"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="difficulty-overlay">
        <h2 className="difficulty-title" id="difficulty-title">{t('difficultyTitle')}</h2>

        <div
          className="difficulty-list"
          role="radiogroup"
          aria-labelledby="difficulty-title"
          onKeyDown={handleKeyDown}
        >
          {DIFFICULTY_ORDER.map(d => {
            const cfg = DIFFICULTIES[d]
            const active = selected === d
            const bubbleId = `difficulty-bubble-${d}`
            return (
              <button
                key={d}
                ref={(el) => { buttonRefs.current[d] = el }}
                type="button"
                role="radio"
                aria-checked={active}
                aria-describedby={active ? bubbleId : undefined}
                tabIndex={active ? 0 : -1}
                className={`difficulty-card difficulty-card--${d} ${active ? 'difficulty-card--active' : ''}`}
                onClick={() => setSelected(d)}
              >
                <span
                  id={bubbleId}
                  className="difficulty-card__bubble"
                  aria-hidden={!active}
                >
                  {difficultyDescription(d)}
                </span>
                <span className="difficulty-card__labels">
                  <span className="difficulty-card__label-ja">{difficultyName(d)}</span>
                  <span className="difficulty-card__label-en">{cfg.label}</span>
                </span>
              </button>
            )
          })}
        </div>

        <div className="difficulty-buttons">
          <button
            className="btn btn--primary btn--large"
            onClick={handleStart}
            disabled={!canPlay}
          >
            {canPlay ? t('difficultyStart') : t('staminaEmpty')}
          </button>
          <button className="btn btn--secondary" onClick={onBack}>
            {t('back')}
          </button>
        </div>
      </div>
    </div>
  )
}
